import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  addToast,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@structural-climbing/ui'
import type { CompetitionCategory } from './types'

interface Props {
  /** The category to remove; null while the modal is closed. */
  category: CompetitionCategory | null
  /** Shown beside the name, since two categories often share one ("Avansati"). */
  label: string
  onClose: () => void
  onRemoved: () => void
  /** Opens the Competitors tab filtered to this category, where they can be moved. */
  onShowCompetitors: (categoryId: string) => void
}

/**
 * Removing an empty category asks once. Removing one with competitors in it is
 * not offered at all: the server refuses it, so the modal explains why and
 * points at the Competitors tab instead of a button that can only fail.
 */
export function RemoveCategoryModal({
  category,
  label,
  onClose,
  onRemoved,
  onShowCompetitors,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [removing, setRemoving] = useState(false)
  // Someone can join between the page loading and Remove being pressed. When
  // the server says so, this flips the modal to the blocked version.
  const [enteredMeanwhile, setEnteredMeanwhile] = useState(false)

  useEffect(() => {
    setEnteredMeanwhile(false)
  }, [category])

  const blocked = !!category && (category.entry_count > 0 || enteredMeanwhile)
  // When it flipped on a 409 the count on hand is stale (0); say at least one.
  const entered = Math.max(category?.entry_count ?? 0, 1)

  async function remove() {
    if (!category) return
    setRemoving(true)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/competition-categories/${category.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { code?: string }
        if (body.code === 'CATEGORY_HAS_ENTRIES') setEnteredMeanwhile(true)
        else addToast({ title: t('competitions.errorSaveFailed'), color: 'danger' })
        return
      }
      addToast({ title: t('competitions.categoryRemoved'), color: 'success' })
      onRemoved()
    } finally {
      setRemoving(false)
    }
  }

  return (
    <Modal scrollBehavior="inside" isOpen={category !== null} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          {blocked
            ? t('competitions.removeCategoryBlockedTitle', { name: label })
            : t('competitions.removeCategoryTitle', { name: label })}
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-foreground/70">
            {blocked
              ? t('competitions.removeCategoryBlockedBody', { count: entered })
              : t('competitions.removeCategoryBody')}
          </p>
        </ModalBody>
        <ModalFooter>
          {blocked ? (
            <>
              <Button variant="light" onPress={onClose}>
                {t('common.close')}
              </Button>
              <Button color="primary" onPress={() => category && onShowCompetitors(category.id)}>
                {t('competitions.showCompetitors')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="light" onPress={onClose} isDisabled={removing}>
                {t('common.cancel')}
              </Button>
              <Button color="danger" isLoading={removing} onPress={() => void remove()}>
                {t('common.remove')}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
